import React, { useEffect, useRef } from 'react';
import { useAppSelector } from '../app/hooks';
import { OrderingSelectors, determineLocationSessions } from 'polygon-ordering';
import { SALE_TYPE } from '../libs/polygon-ordering/src/constants/saleType';
import LocationListItem from './LocationListItem';
import calculateDistanceBetweenCoords from '../utils/calculateDistanceBetweenCoords';
import SectionHeading from './SectionHeading';
import SectionContainer from './SectionContainer';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

const {
  getLocationDeliveryEstimates,
  getOrderingWindowPadding,
  getDeliveryCoordinates,
  getDeliveryRequiresLocationSelection,
} = OrderingSelectors;

const SelectDeliveryLocation = ({
  selectedLocationId,
  setSelectedLocationId,
}: {
  selectedLocationId: string | null;
  setSelectedLocationId: (id: string) => void;
}) => {
  const { t } = useTranslation();

  const sectionRef = useRef<null | HTMLDivElement>(null);
  const locationDeliveryEstimates = useAppSelector(getLocationDeliveryEstimates);
  const locations = useAppSelector(state => state.ordering.locations);
  const orderingWindowPadding = useAppSelector(getOrderingWindowPadding);
  const deliveryRequiresLocationSelection = useAppSelector(getDeliveryRequiresLocationSelection);

  const userLocation = useAppSelector(state => state.currentUserLocation);

  const deliveryLocation = useAppSelector(getDeliveryCoordinates);
  const selectedAddress = useAppSelector(state => state.desiredDeliveryAddress);
  const selectedAddressLocation = selectedAddress && selectedAddress.location;
  const effectiveUserLocation = userLocation ?? deliveryLocation ?? selectedAddressLocation;

  const estimateLocations =
    effectiveUserLocation &&
    locationDeliveryEstimates
      .map(estimate => {
        let location = locations?.[estimate.locationId] as POSLocation | undefined;

        if (!location) {
          return null;
        }

        return {
          ...location,
          ...determineLocationSessions(location, orderingWindowPadding, SALE_TYPE.DELIVERY), // TODO this is missing catering too
          distance: calculateDistanceBetweenCoords(effectiveUserLocation, {
            latitude: location.latitude,
            longitude: location.longitude,
          }),
          bestDeliveryTime: moment(estimate.bestDeliveryTime),
        };
      })
      .filter(location => location != null);

  useEffect(() => {
    if (deliveryRequiresLocationSelection && sectionRef.current) {
      sectionRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [deliveryRequiresLocationSelection]);

  return (
    <div ref={sectionRef}>
      <SectionHeading title={t('title.selectLocation')} containerStyle={styles.sidePadding} />
      <SectionContainer style={{ paddingLeft: 6, paddingRight: 6 }}>
        {estimateLocations?.map((location: any) => (
          <LocationListItem
            location={location}
            key={location.id}
            onClick={() => setSelectedLocationId(location.id)}
            activateBgColor={selectedLocationId === location.id}
          />
        ))}
      </SectionContainer>
    </div>
  );
};

export default SelectDeliveryLocation;

const styles: Styles = {
  sidePadding: {
    paddingLeft: 16,
    paddingRight: 16,
  },
};
